'use client';
import React, { useEffect } from 'react';
import { ModuleWrapper } from './ModuleWrapper';
import { ModuleTable } from './ModuleTable';
import { SessionContext } from '@benzinga/session-context';
import { CalendarManager, Ratings, RatingsAction, getPriceTarget } from '@benzinga/calendar-manager';
import { QuotesManager } from '@benzinga/quotes-manager';
import { DateTime } from 'luxon';
import { TickerCell, PriceChangeBadge, TextCell } from './TableCells';
import type { ColumnsType } from 'antd/es/table';

interface RatingsWithChange extends Ratings {
  changePercent?: number;
}

interface AnalystModuleProps {
  title?: string;
  action?: RatingsAction;
}

const columns: ColumnsType<RatingsWithChange> = [
  {
    key: 'ticker',
    title: 'Ticker',
    dataIndex: 'ticker',
    render: (_value, record) => <TickerCell ticker={record.ticker} name={record.name} />,
  },
  {
    key: 'rating_current',
    title: 'Rating',
    dataIndex: 'rating_current',
    render: (_value, record) => <TextCell>{record.rating_current}</TextCell>,
  },
  {
    key: 'changePercent',
    title: 'Change %',
    dataIndex: 'changePercent',
    render: (_value, record) => (
      <div>
        {record.changePercent !== undefined && <PriceChangeBadge change={record.changePercent} showPercent={false} />}
      </div>
    ),
  },
  {
    key: 'analyst',
    title: 'Analyst Firm',
    dataIndex: 'analyst',
    render: (_value, record) => <TextCell className="font-semibold">{record.analyst}</TextCell>,
  },
  {
    key: 'pt_current',
    title: 'Price Target',
    dataIndex: 'pt_current',
    render: (_value, record) => {
      const priceTarget = getPriceTarget(record);
      return <TextCell>{priceTarget ? `$${Number(priceTarget).toFixed(2)}` : '--'}</TextCell>;
    },
  },
];

const AnalystUpgradesModule: React.FC<AnalystModuleProps> = ({ action = 'Upgrades', title = 'Analyst Upgrades' }) => {
  const session = React.useContext(SessionContext);
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState<RatingsWithChange[]>([]);

  useEffect(() => {
    const fetchAnalystData = async () => {
      try {
        const calendarManager = session.getManager(CalendarManager);
        const ratingsResponse = await calendarManager.getCalendarData(
          'ratings',
          {
            action: action,
            dateFrom: DateTime.now().toFormat('yyyy-LL-dd'),
            pageSize: 10,
          },
          true,
        );

        const symbols = ratingsResponse?.ok?.map(rating => rating.ticker) ?? [];

        if (symbols?.length) {
          const quotesManager = session.getManager(QuotesManager);
          const quoteResponse = await quotesManager.getDelayedQuotes(symbols);

          const ratingsWithChange =
            ratingsResponse?.ok?.map((rating, index) => ({
              ...rating,
              changePercent: quoteResponse?.ok?.[rating.ticker]?.changePercent ?? 0,
              key: `${rating.ticker}-${index}`,
            })) ?? [];

          ratingsWithChange.sort((a, b) => {
            const changeA = Math.abs(a.changePercent ?? 0);
            const changeB = Math.abs(b.changePercent ?? 0);
            return changeB - changeA;
          });

          setData(ratingsWithChange);
        }
      } catch (error) {
        console.error('Error fetching analyst data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalystData();
  }, [session, action]);

  return (
    <>
    <ModuleWrapper title={title}>
      <ModuleTable columns={columns} data={data} loading={loading} emptyText="No analyst data available" />
    </ModuleWrapper>
  </>
  );
};  

export default AnalystUpgradesModule;
